(function () {
  'use strict';

  /*
   * ENTINA Card Showcase V3 bootstrap — 2026-09-17
   *
   * Keep the proven interaction implementation in the versioned backup.
   * This bootstrap adds desktop-only responsive geometry after that script
   * has initialized. Mobile interaction/animation behavior is untouched.
   */

  var currentScript = document.currentScript;

  if (!currentScript || !currentScript.src) {
    return;
  }

  function siblingAsset(filename) {
    try {
      var url = new URL(currentScript.src, window.location.href);
      url.pathname = url.pathname.replace(/[^/]+$/, filename);
      url.search = '';
      url.hash = '';
      return url.href;
    } catch (error) {
      return currentScript.src.replace(
        /entina-card-showcase\.js(?:\?.*)?$/,
        filename
      );
    }
  }

  function installResponsiveGeometry() {
    if (window.__EntinaCardGeometryV3Installed) {
      if (window.EntinaCardGeometryV3Init) {
        window.EntinaCardGeometryV3Init(document);
      }
      return;
    }

    window.__EntinaCardGeometryV3Installed = true;

    var selector = '[data-entina-card-orbit]';
    var desktopQuery = window.matchMedia('(min-width: 750px)');

    function clamp(value, min, max) {
      return Math.min(Math.max(value, min), max);
    }

    function getNumber(value, fallback) {
      var parsed = parseFloat(value);
      return Number.isFinite(parsed) ? parsed : fallback;
    }

    function initSection(section) {
      if (!section || !section.querySelector) {
        return;
      }

      var viewport = section.querySelector('[data-entina-orbit-viewport]');
      var cards = Array.prototype.slice.call(
        section.querySelectorAll('[data-entina-orbit-card]')
      );

      if (!viewport || !cards.length) {
        return;
      }

      if (section.entinaCardGeometryV3Destroy) {
        section.entinaCardGeometryV3Destroy();
      }

      var rafId = null;
      var resizeObserver = null;
      var destroyed = false;

      function clearDesktopSafety() {
        section.style.removeProperty('--entina-card-width-effective');

        var configuredRadius = clamp(
          getNumber(section.dataset.orbitRadius, 350),
          180,
          620
        );

        section.style.setProperty(
          '--entina-orbit-radius',
          configuredRadius + 'px'
        );

        section.removeAttribute('data-entina-geometry-limited');
      }

      function updateGeometry() {
        rafId = null;

        if (destroyed) {
          return;
        }

        if (!desktopQuery.matches) {
          clearDesktopSafety();
          return;
        }

        var sceneWidth = viewport.clientWidth;
        var sceneHeight = viewport.clientHeight;

        if (sceneWidth <= 0 || sceneHeight <= 0) {
          return;
        }

        /*
         * Measure the CSS design target first. The effective override is
         * temporarily removed so a previous safety reduction cannot become
         * the next target and progressively shrink the cards.
         */
        section.style.removeProperty('--entina-card-width-effective');

        var targetCardWidth = cards[0].offsetWidth;

        if (!targetCardWidth) {
          targetCardWidth = 300;
        }

        var targetRadius = clamp(
          getNumber(section.dataset.orbitRadius, 350),
          180,
          620
        );

        /*
         * Radius follows the merchant setting until the section itself is
         * too narrow. This is container-based, not device-name based.
         */
        var radiusByWidth = Math.max(
          180,
          sceneWidth * 0.36
        );

        var effectiveRadius = Math.min(
          targetRadius,
          radiusByWidth,
          620
        );

        var computedViewport = window.getComputedStyle(viewport);
        var perspective = getNumber(computedViewport.perspective, 1450);

        if (!Number.isFinite(perspective) || perspective <= 0) {
          perspective = 1450;
        }

        /*
         * The front card is visually enlarged by perspective plus the
         * active/hover translateZ and scale. Reserve a little more than the
         * current CSS needs so pointer tilt cannot push it into the heading.
         */
        var visualDepth = Math.min(
          effectiveRadius + 55,
          perspective - 180
        );

        var perspectiveScale =
          perspective /
          Math.max(180, perspective - visualDepth);

        var projectedScale = perspectiveScale * 1.08;

        /* 42px top + 42px bottom safety inside the scene. */
        var safeVisualHeight = Math.max(
          280,
          sceneHeight - 84
        );

        /* Card aspect-ratio is width / height = 0.68. */
        var maxCardByHeight =
          safeVisualHeight *
          0.68 /
          projectedScale;

        var effectiveCardWidth = Math.min(
          targetCardWidth,
          maxCardByHeight
        );

        effectiveCardWidth = clamp(
          effectiveCardWidth,
          180,
          targetCardWidth
        );

        section.style.setProperty(
          '--entina-card-width-effective',
          effectiveCardWidth.toFixed(2) + 'px'
        );

        section.style.setProperty(
          '--entina-orbit-radius',
          effectiveRadius.toFixed(2) + 'px'
        );

        var limited =
          effectiveCardWidth < targetCardWidth - 1 ||
          effectiveRadius < targetRadius - 1;

        if (limited) {
          section.setAttribute('data-entina-geometry-limited', 'true');
        } else {
          section.removeAttribute('data-entina-geometry-limited');
        }
      }

      function scheduleGeometry() {
        if (destroyed || rafId) {
          return;
        }

        rafId = window.requestAnimationFrame(updateGeometry);
      }

      if ('ResizeObserver' in window) {
        resizeObserver = new ResizeObserver(scheduleGeometry);
        resizeObserver.observe(viewport);
        resizeObserver.observe(section);
      }

      window.addEventListener('resize', scheduleGeometry, { passive: true });

      scheduleGeometry();

      section.entinaCardGeometryV3Destroy = function () {
        destroyed = true;

        if (rafId) {
          window.cancelAnimationFrame(rafId);
          rafId = null;
        }

        if (resizeObserver) {
          resizeObserver.disconnect();
          resizeObserver = null;
        }

        window.removeEventListener('resize', scheduleGeometry);

        section.style.removeProperty('--entina-card-width-effective');
        section.removeAttribute('data-entina-geometry-limited');
        section.entinaCardGeometryV3Destroy = null;
      };
    }

    function initAll(root) {
      var scope = root && root.querySelectorAll ? root : document;

      if (scope.matches && scope.matches(selector)) {
        initSection(scope);
      }

      scope.querySelectorAll(selector).forEach(initSection);
    }

    window.EntinaCardGeometryV3Init = initAll;

    initAll(document);

    document.addEventListener('shopify:section:load', function (event) {
      initAll(event.target);
    });

    document.addEventListener('shopify:section:unload', function (event) {
      var sections = [];

      if (event.target.matches && event.target.matches(selector)) {
        sections.push(event.target);
      }

      if (event.target.querySelectorAll) {
        sections = sections.concat(
          Array.prototype.slice.call(
            event.target.querySelectorAll(selector)
          )
        );
      }

      sections.forEach(function (section) {
        if (section.entinaCardGeometryV3Destroy) {
          section.entinaCardGeometryV3Destroy();
        }
      });
    });

    document.addEventListener('shopify:block:select', function (event) {
      var section = event.target && event.target.closest
        ? event.target.closest(selector)
        : null;

      if (section) {
        initSection(section);
      }
    });

    document.addEventListener('shopify:block:deselect', function (event) {
      var section = event.target && event.target.closest
        ? event.target.closest(selector)
        : null;

      if (section) {
        initSection(section);
      }
    });

    if (desktopQuery.addEventListener) {
      desktopQuery.addEventListener('change', function () {
        initAll(document);
      });
    } else if (desktopQuery.addListener) {
      desktopQuery.addListener(function () {
        initAll(document);
      });
    }
  }

  function loadBaseInteraction() {
    if (window.__EntinaCardBaseInteractionLoaded) {
      installResponsiveGeometry();
      return;
    }

    window.__EntinaCardBaseInteractionLoaded = true;

    var baseScript = document.createElement('script');
    baseScript.src = siblingAsset('entina-card-showcase-base-20260917.js');
    baseScript.async = false;

    baseScript.onload = function () {
      installResponsiveGeometry();
    };

    baseScript.onerror = function () {
      window.__EntinaCardBaseInteractionLoaded = false;
      console.error('ENTINA card showcase base interaction failed to load.');
    };

    document.head.appendChild(baseScript);
  }

  loadBaseInteraction();
})();
